import { Location } from '@angular/common';
import { Component, DoCheck, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivationEnd, NavigationEnd, provideRouter, Router, withRouterConfig } from '@angular/router';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { GlobalVariablesService } from 'app/services/global-variables.service';
import { CiWorkstationComponent } from 'app/shared/ci-workstation/ci-workstation.component';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { pcgSettings } from 'app/shared/generated/pcg-settings';
import { NavDividerCharacter, NavigationService } from 'app/shared/navigation/navigation.service';
import { TopClickNavComponent } from 'app/shared/navigation/top-click-nav/top-click-nav.component';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { GlobalService } from 'app/shared/services/global.service';
import { SpinnerService } from '../spinner/spinner.service';
import { SysInventoryService } from 'app/formulary-inventory/services/sys-inventory.service';

@UntilDestroy()
@Component({
    selector: 'pcg-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '(document:click)': 'clickDoc($event)',
        '(window:resize)': 'onResize()'
    }
})
export class TopHeaderComponent implements OnInit, DoCheck {
    infoService = inject(QuickUserInfoService);
    spinner = inject(SpinnerService);
	navService = inject(NavigationService);
    sysInvService = inject(SysInventoryService);

	@ViewChild(TopClickNavComponent, { static: true }) nav!: TopClickNavComponent;
	@ViewChild('userNav') userNav!: ElementRef;
	@ViewChild('mainHamburger', { static: true }) mainHamburger!: ElementRef;

	@Input() tabPanel: any;
	@Output() hamburgerClick = new EventEmitter<boolean>();

	user!: UserJsVm;
    userSignal = signal<UserJsVm>(null!);
    userInitals = computed(() => {
        return `${this.userSignal()?.firstName!.substring(0, 1)}${this.userSignal()?.lastName!.substring(0, 1)}`
    });
    currentUserUrl = 'admin/users/user/edit/details/currentUser';

    currForceSelect!: string;
    currMainNavMenu!: string;	
	mode = pcgSettings.mode;
	navDividerCharacter = NavDividerCharacter;

	hrefLocation: string = "";

	userNavOpen = false;
	adminAccess = false;	
	isMobile = false;
	isLoggedOn = false;
	isNavFixed = signal<boolean>(false);
	isCiUser = false;
	hasCiWorkstation = false;
	hasCiAccess = false;
	isCiArea = false;

    isInInventory = signal<boolean>(false);
    hasPerms = signal<boolean>(false);
    showInvWorkstationBanner = computed(() => 
        this.isInInventory()
        && (this.gvs.invName() != '' || this.hasPerms())
    );

	navigationOffset = 0;

	faIconName = { faBars };

    loadingDt = {
        root: {
            background: "#465666",
            value: {
                background: "#339cd6"
            }
        }
    };

	constructor(
		private readonly sec: SecurityService
		, private readonly router: Router
		, private readonly modalService: NgbModal
		, private readonly location: Location
		, private readonly gvs: GlobalVariablesService
		, public globalService: GlobalService
	) {
		// Subscribe to route changes and update the navs when they happen 💯
		this.router.events
			.pipe(untilDestroyed(this))
			.pipe(filter((route): route is ActivationEnd => 
                route instanceof ActivationEnd 
                && route.snapshot.component != null)
            ).subscribe((route) => { 
                this.setHasCiAccess(); 
                this.setInvAccess(route);
            }
		);
    }

    // Close the main navigation when they click outside of it
	// and are not clicking the mobile hamburger button
	clickDoc(event: { target: any; }) {
		if (
			!this.mainHamburger?.nativeElement.contains(event.target)
			&& !this.nav?.elRef?.nativeElement.contains(event.target)
			&& !this.userNav?.nativeElement.contains(event.target)
		) { 
			this.navService.setCurrOpenNavMenu(''); 
			this.userNavOpen = false;
		}
	}

	// Fix the nav offset on resize
	onResize() { 
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.globalService.setNavHeight();
    }

	ngOnInit() {
        this.userSignal.set(this.sec.getUser());
		this.isMobile = GlobalService.setIsMobile(window.innerWidth);
		this.sec.user$.pipe(untilDestroyed(this)).subscribe(user => { this.user = user; });
		setTimeout(() => {
			let secondaryNav = document.getElementById("secondaryNav");
			if (secondaryNav != null) { secondaryNav.scrollLeft = Number(localStorage?.getItem("subNavPosition")); }
		}, 250);

		localStorage.getItem("isNavFixed") === "true" 
			? this.isNavFixed.set(true) 
			: this.isNavFixed.set(false);

        this.setHasCiAccess();
        this.setInvAccess();
	}

    setInvAccess(routerEvent: ActivationEnd | null = null) {
        this.hasPerms.set(this.sec?.hasModuleAccess(
            [BusinessAreaEnum.SysInv]
            , SecurityService.setMinRole(PermissionRole.Technician)));

        this.isInInventory.set(this.location?.path()?.includes('inv/'));
        
        // set the inventory name that appears on the workstation banner
        if (
            this.isInInventory()
            && routerEvent?.snapshot?.params?.inventoryId != null
        ) {
            var routeId = routerEvent.snapshot.params.inventoryId;
            if (this.gvs.sysInvInventoryId() == null || routeId != this.gvs.sysInvInventoryId()) {
                this.sysInvService
                    .getInvNameById(routeId)
                    .subscribe((data) => { 
                        this.gvs.invName.set(data?.text ?? ''); 
                        this.gvs.sysInvInventoryId.set(routeId);
                        this.globalService.isBannerDisplayed.set(this.showInvWorkstationBanner());
                    }
                );
            }
            
        } else {
            this.gvs.invName.set('');
            this.gvs.sysInvInventoryId.set(null);
            this.globalService.isBannerDisplayed.set(this.showInvWorkstationBanner());
        }
    }

    setHasCiAccess() {
        this.isCiArea = this.location?.path()?.includes('ci-repack');
        this.hasCiAccess = this.isCiArea 
            && this.sec?.hasModuleAccess( 
                [ BusinessAreaEnum.CI ]
                , SecurityService.setMinRole(PermissionRole.Technician)
            );
        this.hasCiAccess 
            ? this.globalService.isBannerDisplayed.set(true) 
            : this.globalService.isBannerDisplayed.set(false); 
        if (
            this.hasCiAccess 
            && !this.location.path().includes('/job-sheet/')
        ) { this.getCiWorkstation(); }
     }

	ngDoCheck() { 
        this.isLoggedOn = this.sec.isLoggedOn(); //Needed to check if user is logged in (to toggle header)        
        this.globalService.setNavHeight();
    }	
	setSubNavPosition() { localStorage.setItem("subNavPosition", document.getElementById("secondaryNav")!.scrollLeft.toString()); }
	toggleNav() { this.hamburgerClick.emit(true); } // Toggle whether or not the main navigation is open
	openCiWorkstationModal() { this.modalService.open(CiWorkstationComponent, { animation: false }); }	

	getCiWorkstation() {
		// Check if is a CI user
		this.isCiUser = this.sec?.hasModuleAccess(
			[ BusinessAreaEnum.CI ]
			, SecurityService.setMinRole(PermissionRole.User)
		);

		if (this.isCiUser === true) {
			const opened = localStorage.getItem('ciWorkstationModalOpened');
			const sessionToken = localStorage.getItem('jwt');
			this.hasCiWorkstation = false;
			if (
				opened !== sessionToken 
				&& this.user.isInternalBphpUser
				&& !this.user.email!.includes("@paulconsulting.com")
			) { this.openCiWorkstationModal(); }
			localStorage.setItem('ciWorkstationModalOpened', sessionToken!);
		}
	}

	setDynamicStyle(
		name: string
		, styles: string
	) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		dynamicStyle = document.createElement('style');
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

    mainNavTitle = computed(() => {
		let name = this.navService.currNav()?.name;

		switch (name) {
			case "Edit Agency": {
				name = this.gvs.herosAgencyName();
				break;
			}
			default: { break; }
		}

		if (name?.length <= 0) { name = this.navService.currNav()?.name; }
		return name;
	});
}
